import type { Vec3 } from '../types';
import { SurfaceType } from '../types';
import { SURFACE_PROPS, BALL_RADIUS, BALL_REST_SPEED, BALL_REST_TIME, GRAVITY } from '../constants';
import { createBallBody, stepWorld, removeBallBody } from './RapierWorld';
import type RAPIER from '@dimforge/rapier3d-compat';

/** Flat-turf contact model: Rapier handles bounce and spin/slip transfer;
 * explicit rolling resistance handles energy lost deforming turf. */
export class GroundPhysics {
  private ballBody: RAPIER.RigidBody | null = null;
  private restTimer = 0;
  private _atRest = false;
  private rollingDeceleration = 0;
  private settledDrag = 0;
  private origin: Vec3 = { x: 0, y: 0, z: 0 };

  get atRest(): boolean { return this._atRest; }

  startGroundPhase(position: Vec3, velocity: Vec3,
    surfaceType: SurfaceType = SurfaceType.Fairway, spinRate = 0,
    spinAxis: Vec3 | number = { x: -1, y: 0, z: 0 },
  ): void {
    this.cleanup();
    const props = SURFACE_PROPS[surfaceType];
    this.rollingDeceleration = props.rollingResistance * GRAVITY;
    this.settledDrag = props.settledDrag;
    this.origin = { x: position.x, y: 0, z: position.z };
    // Compatibility for callers supplying tilt degrees; orient relative to travel.
    const direction = Math.atan2(velocity.x, velocity.z);
    const tilt = typeof spinAxis === 'number' ? spinAxis * Math.PI / 180 : 0;
    const axis = typeof spinAxis === 'number' ? {
      x: -Math.cos(tilt) * Math.cos(direction), y: Math.sin(tilt),
      z: Math.cos(tilt) * Math.sin(direction),
    } : spinAxis;
    const omega = Math.max(0, spinRate) * 2 * Math.PI / 60;
    // Flight/render coordinates are height above surface. Rapier tracks sphere center.
    // Flat terrain and noninteracting comparison balls allow a local origin:
    // avoids float32 contact/position drift depending on landing distance.
    this.ballBody = createBallBody({ x: 0, y: Math.max(position.y, 0) + BALL_RADIUS, z: 0 },
      velocity, props, { x: axis.x * omega, y: axis.y * omega, z: axis.z * omega });
  }

  step(dt: number): Vec3 {
    if (!this.ballBody || this._atRest) return this.getPosition();
    stepWorld();
    const body = this.ballBody;
    const v = body.linvel();
    const omega = body.angvel();
    const nearGround = body.translation().y <= BALL_RADIUS + 0.003 && Math.abs(v.y) < 0.25;
    const horizontalSpeed = Math.hypot(v.x, v.z);
    // Turf deformation resists settled motion, including residual slip. Scale
    // linear and angular speeds together so this term can only remove energy;
    // the contact solver remains responsible for slip-to-roll spin transfer.
    if (nearGround && horizontalSpeed > 0) {
      // Exact speed decay for dv/dt = -a - k*v, clamped at zero.
      const decay = Math.exp(-this.settledDrag * dt);
      const nextSpeed = this.settledDrag > 0
        ? (horizontalSpeed + this.rollingDeceleration / this.settledDrag) * decay - this.rollingDeceleration / this.settledDrag
        : horizontalSpeed - this.rollingDeceleration * dt;
      const scale = Math.max(0, nextSpeed / horizontalSpeed);
      body.setLinvel({ x: v.x * scale, y: v.y, z: v.z * scale }, true);
      body.setAngvel({ x: omega.x * scale, y: omega.y * scale, z: omega.z * scale }, true);
    }
    const settledVelocity = body.linvel();
    const speed = Math.hypot(settledVelocity.x, settledVelocity.y, settledVelocity.z);
    const surfaceSpinSpeed = BALL_RADIUS * Math.hypot(omega.x, omega.z);
    if (nearGround && speed < BALL_REST_SPEED && surfaceSpinSpeed < BALL_REST_SPEED) {
      this.restTimer += dt;
      if (this.restTimer >= BALL_REST_TIME) {
        this._atRest = true;
        body.setLinvel({ x: 0, y: 0, z: 0 }, true);
        body.setAngvel({ x: 0, y: 0, z: 0 }, true);
        body.sleep();
      }
    } else this.restTimer = 0;
    // No teleport rescue and no elapsed-time freeze. A moving ball keeps moving.
    return this.getPosition();
  }

  getPosition(): Vec3 {
    if (!this.ballBody) return { x: 0, y: 0, z: 0 };
    const t = this.ballBody.translation();
    return { x: this.origin.x + t.x, y: Math.max(t.y - BALL_RADIUS, 0), z: this.origin.z + t.z };
  }

  getVelocity(): Vec3 {
    if (!this.ballBody) return { x: 0, y: 0, z: 0 };
    const v = this.ballBody.linvel();
    return { x: v.x, y: v.y, z: v.z };
  }

  getAngularVelocity(): Vec3 {
    if (!this.ballBody) return { x: 0, y: 0, z: 0 };
    const v = this.ballBody.angvel();
    return { x: v.x, y: v.y, z: v.z };
  }

  cleanup(): void {
    if (this.ballBody) { removeBallBody(this.ballBody); this.ballBody = null; }
    this._atRest = false;
    this.restTimer = 0;
  }
}
